import React, { createContext, useReducer, useContext, useMemo } from 'react';
import get from 'lodash/get';
import { MessageType, ActionType, PageError } from '../models/common';

export const InitialState = {
  type: MessageType.Success,
  pageLoading: false,
  pageFetching: false,
  showPrivacy: false,
  showTerms: false,
  headerVisible: false,
  pageTitle: '',
  categories: [],
};

export const MessagesContext = createContext(InitialState);
export const useMessages = () => useContext(MessagesContext);

const createSetMessageAction = (message, type = MessageType.Info) => ({
  type: ActionType.SetMessage,
  payload: {
    type,
    message,
  },
});

export const getErrorMessage = (err) => {
  return get(
    err,
    'response.data.error',
    get(err, 'response.data.message', get(err, 'message', 'There was an error'))
  );
};

export const reducer = (state, { type, payload }) => {
  switch (type) {
    case ActionType.SetMessage:
      if (payload.type && payload.message) {
        return {
          ...state,
          message: payload.message,
          type: payload.type,
        };
      }
      break;

    case ActionType.ClearMessage:
      return {
        ...state,
        message: undefined,
      };
    case ActionType.SetPageLoading:
    case ActionType.SetPageTitle:
    case ActionType.SetPageFetching:
      return {
        ...state,
        ...payload,
      };

    case ActionType.SetPageError:
      return {
        ...state,
        pageError: payload,
        pageLoading: false,
        pageFetching: false,
      };

    case ActionType.ClearPageError:
      return {
        ...state,
        pageError: undefined,
      };

    default:
      throw new Error(`Messages action type not found: ${type}`);
  }

  return state;
};

const MessagesProvider = (props) => {
  const [state, dispatch] = useReducer(reducer, InitialState);

  const dispatchFunctions = useMemo(
    () => ({
      setSuccessMessage: (message) =>
        dispatch(createSetMessageAction(message, MessageType.Success)),
      setErrorMessage: (message, err) =>
        dispatch(
          createSetMessageAction(
            `${message || ''}${err ? `${getErrorMessage(err)}` : ''}`,
            MessageType.Error
          )
        ),
      clearMessage: () => dispatch({ type: ActionType.ClearMessage }),
      setPageTitle: (pageTitle) =>
        dispatch({ type: ActionType.SetPageTitle, payload: { pageTitle } }),
      setPageLoading: (pageLoading) =>
        dispatch({ type: ActionType.SetPageLoading, payload: { pageLoading } }),
      setPageFetching: (pageFetching, pageFetchingMessage) =>
        dispatch({
          type: ActionType.SetPageFetching,
          payload: { pageFetching, pageFetchingMessage },
        }),
      setPageNotFound: () =>
        dispatch({ type: ActionType.SetPageError, payload: PageError.NotFound }),
      setPageForbidden: () =>
        dispatch({ type: ActionType.SetPageError, payload: PageError.Forbidden }),
      clearPageError: () => dispatch({ type: ActionType.ClearPageError }),
    }),
    [dispatch]
  );

  return (
    <MessagesContext.Provider
      {...props}
      value={{
        ...state,
        ...dispatchFunctions,
      }}
    />
  );
};

export default MessagesProvider;
